import { Fragment } from 'react';
import styles from './MarketHealth.module.css';
import { ZONE_COLORS } from './riskScale';
import { STRATEGY_CONFIGS } from './strategyConfigs';

// Clamp a percentage so a moving label near an extreme never overflows the
// track edge; the pointer still points at the true fill position.
const clampPct = (pct) => Math.max(4, Math.min(96, pct));

const DIMENSIONS = [
  { key: 'breadth', label: 'Market Participation' },
  { key: 'volatility', label: 'Volatility Regime' },
  { key: 'creditSafety', label: 'Credit Conditions' },
  { key: 'yieldCurve', label: 'Monetary Environment' },
  { key: 'trendRotation', label: 'Trend & Rotation' },
];

const MAX_POINTS = 25;

// Conservative strategy binary confirms — 5 yes/no signals.
const CONSERVATIVE_CONFIRMS = [
  { key: 'ycInversion', label: 'Prior Monetary Stress' },
  { key: 'breadthMom', label: 'Participation Momentum' },
  { key: 'creditP15', label: 'Credit Stress · Severe' },
  { key: 'creditP20', label: 'Credit Stress · Moderate' },
  { key: 'breadthLt30', label: 'Low Participation' },
];

// Fill color follows the dimension's OWN score (0-25), not the composite
// zone — spec §9.5: "0-8 red, 9-14 yellow, 15-25 green."
const colorForDimensionScore = (value) => {
  if (value <= 8) return ZONE_COLORS.red;
  if (value <= 14) return ZONE_COLORS.yellow;
  return ZONE_COLORS.green;
};

// Fill color for strategy features (0-100, higher = more stress)
const colorForFeatureScore = (value) => {
  if (value >= 70) return ZONE_COLORS.red;
  if (value >= 40) return ZONE_COLORS.yellow;
  return ZONE_COLORS.green;
};

/**
 * Five horizontal bars showing each dimension's contribution out of 25
 * points (spec §9.5, plus Yield Curve per v2 spec §2.5), OR the per-
 * strategy factors when `mode` and the corresponding data are present.
 *
 * Adaptive mode: 5 feature bars (0-100 each), higher = more stress.
 * Conservative mode: 5 binary confirm indicators.
 * Default (no mode or missing data): classic dimension bars.
 */
const DimensionBars = ({ dimensions = {}, mode, score, adaptiveFeatures, conservativeConfirmFlags, triggerActive = false }) => {
  // ── Risk Pressure bar (susceptibility strategies) ──
  //
  // This is the single consolidated risk read for the card — the former gauge
  // and this bar showed the SAME backend score, so the gauge was dropped. The
  // track is anchored to the strategy's full domain (0..100), NOT the
  // threshold, so:
  //   - the fill can rise ABOVE the trigger line toward the real maximum,
  //   - the threshold marker sits partway along (boundary / max),
  //   - the exact score rides ABOVE the fill (fast-glance number), while the
  //     threshold marker + its value sit BELOW — separate lanes, never overlap.
  const config = STRATEGY_CONFIGS[mode];
  const isNum = (v) => typeof v === 'number' && Number.isFinite(v);
  if (config && config.features) {
    const threshold = config.riskBoundary;
    const [domainMin, domainMax] = config.scoreDomain;
    const span = domainMax - domainMin || 1;
    const { features } = config;

    // Factor breakdown is only available when the backend features are present
    // (older/backfill rows may omit them). The headline score + threshold
    // marker DON'T depend on features — they render from the canonical score
    // alone, so the consolidated bar always shows the fast-glance number.
    const segments = adaptiveFeatures
      ? features.map(({ key, label, weight }) => {
        const raw = adaptiveFeatures[key];
        const value = isNum(raw) ? Math.max(0, Math.min(100, raw)) : null;
        const contribution = value !== null ? (weight * value) : 0;
        return { key, label, weight, value, contribution, color: value !== null ? colorForFeatureScore(value) : '#e9ecef' };
      })
      : [];
    const activeWeight = segments.reduce((s, f) => s + (f.value !== null ? f.weight : 0), 0);

    // Prefer the canonical score passed from the page; fall back to the
    // locally-weighted susceptibility only when no score was passed AND
    // features exist to compute one.
    const localSusceptibility = activeWeight > 0
      ? segments.reduce((s, f) => s + f.contribution, 0) / activeWeight
      : null;
    const susceptibility = isNum(score) ? score : localSusceptibility;
    const hasScore = isNum(susceptibility);
    const breached = hasScore && susceptibility >= threshold;

    // Positions as a % of the FULL domain (0..100), so the trigger line sits
    // partway along and the fill has real room above it.
    const scorePct = hasScore ? Math.max(0, Math.min(100, ((susceptibility - domainMin) / span) * 100)) : 0;
    const thresholdPct = Math.max(0, Math.min(100, ((threshold - domainMin) / span) * 100));

    const sorted = [...segments].filter((s) => s.value !== null).sort((a, b) => b.contribution - a.contribution);

    return (
      <div className={styles.pressureContainer}>
        <div className={styles.pressureHeader}>
          <span className={styles.pressureTitle}>Risk Pressure</span>
        </div>

        {/* Value lane (above the track): the exact score, pinned over the fill. */}
        <div className={styles.pressureValueLane}>
          {hasScore && (
            <span
              className={breached ? styles.pressureValueDanger : styles.pressureValueSafe}
              style={{ left: `${clampPct(scorePct)}%` }}
            >
              {susceptibility.toFixed(1)}
              <span className={styles.pressureValuePointer} aria-hidden="true" />
            </span>
          )}
        </div>

        <div
          className={styles.pressureTrack}
          role="progressbar"
          aria-valuemin={domainMin}
          aria-valuemax={domainMax}
          aria-valuenow={hasScore ? Number(susceptibility.toFixed(1)) : undefined}
          aria-label={`Risk pressure ${hasScore ? susceptibility.toFixed(1) : 'unavailable'} of ${domainMax}; ${config.riskBoundaryLabel}`}
        >
          <div className={styles.pressureBarArea}>
            {sorted.map((seg) => {
              // Segment width in DOMAIN space: its share of the score, times
              // the fill's domain fraction.
              const shareOfScore = hasScore && susceptibility > 0
                ? (seg.contribution / activeWeight) / susceptibility
                : 0;
              const widthPct = shareOfScore * scorePct;
              return (
                <div
                  key={seg.key}
                  className={styles.pressureSegment}
                  style={{ width: `${widthPct}%`, backgroundColor: seg.color }}
                  title={`${seg.label}: ${seg.value.toFixed(0)} × ${seg.weight.toFixed(3)} = ${(seg.contribution / activeWeight).toFixed(1)}`}
                />
              );
            })}
          </div>
          {/* Threshold marker + its value in the lane BELOW the track. */}
          <div className={styles.pressureThresholdMark} style={{ left: `${thresholdPct}%` }}>
            <span className={styles.pressureThresholdValue}>{config.riskBoundaryLabel}</span>
          </div>
        </div>

        {sorted.length > 0 && (
          <div className={styles.pressureLegend}>
            {sorted.map((seg) => {
              const contrib = activeWeight > 0 ? seg.contribution / activeWeight : 0;
              return (
                <div key={seg.key} className={styles.pressureLegendRow}>
                  <span className={styles.pressureLegendDot} style={{ backgroundColor: seg.color }} />
                  <span className={styles.pressureLegendLabel}>{seg.label}</span>
                  <span className={styles.pressureLegendValue}>{contrib.toFixed(1)}</span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    );
  }

  // ── Conservative: vertical threat ladder ──
  // Active flags stack from the bottom up like rising pressure. The risk
  // threshold line sits at the config boundary (2 of 5); reaching it puts the
  // reading in the risk band.
  if (mode === 'conservative' && conservativeConfirmFlags) {
    const REQUIRED = STRATEGY_CONFIGS.conservative?.riskBoundary ?? 2;
    const items = CONSERVATIVE_CONFIRMS.map(({ key, label }) => ({
      key, label, active: conservativeConfirmFlags[key] === true,
    }));

    // Bottom-up: active items at the bottom (pressure rising), inactive above.
    const inactive = items.filter((c) => !c.active);
    const active = items.filter((c) => c.active);
    const bottomUp = [...inactive, ...active];
    const dividerIndex = bottomUp.length - REQUIRED;

    const ladderContentClass = [
      styles.threatLadderContent,
      !triggerActive ? styles.threatLadderDimmed : '',
    ].filter(Boolean).join(' ');

    // The tooltip lives OUTSIDE the dimmed content wrapper: a parent
    // `opacity` applies to its whole subtree and can't be undone by a child,
    // which is why the tooltip used to render half-transparent. Keeping it a
    // sibling (both inside the positioned `.threatLadder`) lets it stay fully
    // opaque while the ladder steps below it are muted.
    return (
      <div className={styles.threatLadder} role="list" aria-label="Sell conditions">
        <div className={ladderContentClass}>
          {bottomUp.map(({ key, label, active: isActive }, i) => (
            <Fragment key={key}>
              {i === dividerIndex && (
                <div className={styles.threatDivider}>
                  <span className={styles.threatDividerLine} />
                  <span className={styles.threatDividerLabel}>
                    {STRATEGY_CONFIGS.conservative?.riskBoundaryLabel ?? 'Sell trigger'}
                  </span>
                  <span className={styles.threatDividerLine} />
                </div>
              )}
              <div
                role="listitem"
                className={[
                  styles.threatStep,
                  isActive ? styles.threatStepActive : styles.threatStepInactive,
                ].join(' ')}
              >
                <span className={styles.threatDot} style={{ backgroundColor: isActive ? ZONE_COLORS.red : '#d1d5db' }} />
                <span className={styles.threatLabel}>{label}</span>
                {isActive && <span className={styles.threatBadge}>Active</span>}
              </div>
            </Fragment>
          ))}
        </div>
        {!triggerActive && (
          <div className={styles.threatLadderTooltip} role="tooltip">
            These conditions are being monitored but the price gate is not active
            — no trade is possible until SPY declines past the trigger level.
          </div>
        )}
      </div>
    );
  }

  // ── Default: classic dimension bars (0-25 each) ──
  return (
    <div className={styles.dimensionBars}>
      {DIMENSIONS.map(({ key, label }) => {
        const raw = dimensions?.[key];
        const hasValue = typeof raw === 'number' && Number.isFinite(raw);
        const value = hasValue ? Math.max(0, Math.min(MAX_POINTS, raw)) : 0;
        const pct = (value / MAX_POINTS) * 100;
        const color = colorForDimensionScore(value);

        return (
          <div key={key} className={styles.dimensionRow}>
            <span className={styles.dimensionLabel}>{label}</span>
            <div
              className={styles.dimensionTrack}
              role="progressbar"
              aria-label={label}
              aria-valuemin={0}
              aria-valuemax={MAX_POINTS}
              aria-valuenow={hasValue ? value : undefined}
            >
              {hasValue && (
                <div
                  className={styles.dimensionFill}
                  style={{ width: `${pct}%`, backgroundColor: color }}
                />
              )}
            </div>
            <span className={styles.dimensionValue}>
              {hasValue ? `${value.toFixed(1)} / ${MAX_POINTS}` : '—'}
            </span>
          </div>
        );
      })}
    </div>
  );
};

export default DimensionBars;
